import { Component, OnInit, OnDestroy, NgZone } from "@angular/core";
import { WebSocketService } from "../web-socket.service";
import { DriversService } from "../drivers/drivers.service";
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from "ngx-toastr";

declare const google: any;

@Component({
  selector: "app-online-drivers",
  templateUrl: "./online-drivers.component.html",
  styleUrls: ["./online-drivers.component.sass"],
})
export class OnlineDriversComponent implements OnInit, OnDestroy {
  private map!: google.maps.Map;

  /** driverId → marker */
  private markers = new Map<string, google.maps.Marker>();

  /** Debounce timer */
  private idleTimer: any;

  /** Default center (Surat) */
  private defaultCenter = { lat: 18.5246091, lng: 73.8786239 };

  private radiusCircle!: google.maps.Circle;
  private dashOffset = 0;
  private dashInterval: any;
  private autocomplete: any;
  constructor(
    private ws: WebSocketService,
    private zone: NgZone,
    private driverService: DriversService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  /* ----------------------------------
   * LIFECYCLE
   * ---------------------------------- */

  selectedDriver: any = null;
  showDriverPanel = false;
  driverData: any;
  ngOnInit(): void {
    this.initMap();
    this.listenNearbyDrivers();
    this.listenDriverUpdates();
    // this.ws.connect();
  }

  ngOnDestroy(): void {
    this.markers.forEach((m) => m.setMap(null));
    this.markers.clear();
  }

  /* ----------------------------------
   * MAP INIT
   * ---------------------------------- */
  centerMarker: any;

  // private initMap(): void {
  //   this.map = new google.maps.Map(document.getElementById("map")!, {
  //     center: this.defaultCenter,
  //     zoom: 13,
  //     minZoom: 10,
  //     maxZoom: 17,
  //     disableDefaultUI: true,
  //   });

  //   this.map.addListener("idle", () => {
  //     this.onMapIdle();
  //   });
  // }

  private initMap(): void {
    this.map = new google.maps.Map(document.getElementById("map")!, {
      center: this.defaultCenter,
      zoom: 13,
      minZoom: 10,
      maxZoom: 17,
      disableDefaultUI: true,
    });

    this.initAutocomplete();
    this.map.addListener("click", () => {
      this.zone.run(() => {
        this.closeDriverPanel();
      });
    });

    this.map.addListener("idle", () => {
      this.onMapIdle();
    });
  }
  private initAutocomplete(): void {
    const input = document.getElementById("searchInput") as HTMLInputElement;

    this.autocomplete = new google.maps.places.Autocomplete(input, {
      fields: ["geometry", "formatted_address"],
    });

    this.autocomplete.addListener("place_changed", () => {
      const place = this.autocomplete.getPlace();

      if (!place.geometry || !place.geometry.location) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      console.log("[Search] Selected location:", { lat, lng });

      // 🎯 Move map
      this.map.panTo({ lat, lng });
      this.map.setZoom(14);

      // 🔴 Trigger driver fetch
      this.onMapIdle();
    });
  }

  /* ----------------------------------
   * MAP IDLE HANDLER
   * ---------------------------------- */
  private onMapIdle(): void {
    const center = this.map.getCenter();
    const zoom = this.map.getZoom();

    if (!center || !zoom) return;

    clearTimeout(this.idleTimer);

    this.idleTimer = setTimeout(() => {
      const lat = center.lat();
      const lng = center.lng();
      const radiusKm = this.radiusFromZoom(zoom);

      console.log("[Map] Fetch drivers", { lat, lng, zoom, radiusKm });
      // 🔴 DRAW / UPDATE RADIUS
      this.updateRadiusCircle(center, radiusKm, zoom);
      this.ws.getNearbyDrivers(lat, lng, radiusKm);
    }, 500);
  }

  /* ----------------------------------
   * ZOOM → RADIUS LOGIC
   * ---------------------------------- */
  private radiusFromZoom(zoom: number): number {
    if (zoom >= 15) return 2;
    if (zoom >= 13) return 5;
    if (zoom >= 11) return 10;
    return 20;
  }

  /* ----------------------------------
   * INITIAL DRIVER MARKERS
   * ---------------------------------- */
  private listenNearbyDrivers(): void {
    this.ws.onNearbyDriversResult().subscribe((res: any) => {
      if (!res?.drivers?.length) return;

      this.zone.runOutsideAngular(() => {
        const activeIds = new Set<string>();

        res.drivers.forEach((driver: any) => {
          activeIds.add(driver.driverId);

          if (this.markers.has(driver.driverId)) return;

          // const marker = new google.maps.Marker({
          //   map: this.map,
          //   position: { lat: driver.lat, lng: driver.lng },
          //   icon: {
          //     url: "assets/images/vehicleIcon.png",
          //     scaledSize: new google.maps.Size(32, 42),
          //   },
          // });
          // this.markers.set(driver.driverId, marker);
          const marker = new google.maps.Marker({
            map: this.map,
            position: { lat: driver.lat, lng: driver.lng },
            icon: {
              url: "assets/images/vehicleIcon.png",
              scaledSize: new google.maps.Size(32, 42),
            },
          });

          // 👉 Marker click
          marker.addListener("click", () => {
            this.zone.run(() => {
              this.selectedDriver = driver;
              this.showDriverPanel = true;
              this.getDriver(driver.driverId);
            });
          });

          this.markers.set(driver.driverId, marker);
        });

        // 🧹 Remove drivers out of radius
        this.markers.forEach((marker, driverId) => {
          if (!activeIds.has(driverId)) {
            marker.setMap(null);
            this.markers.delete(driverId);
          }
        });
      });
    });
  }

  /* ----------------------------------
   * REALTIME DRIVER UPDATES
   * ---------------------------------- */
  private listenDriverUpdates(): void {
    this.ws.onDriverLocation().subscribe((data: any) => {
      const marker = this.markers.get(data.driverId);
      if (!marker) return;

      this.moveMarker(marker, data.lat, data.lng);
    });
  }

  /* ----------------------------------
   * SMOOTH MARKER MOVEMENT
   * ---------------------------------- */
  private moveMarker(
    marker: google.maps.Marker,
    lat: number,
    lng: number
  ): void {
    const start = marker.getPosition();
    if (!start) return;

    const end = new google.maps.LatLng(lat, lng);

    const steps = 12;
    let step = 0;

    const dLat = (end.lat() - start.lat()) / steps;
    const dLng = (end.lng() - start.lng()) / steps;

    const interval = setInterval(() => {
      step++;

      marker.setPosition({
        lat: start.lat() + dLat * step,
        lng: start.lng() + dLng * step,
      });

      if (step >= steps) clearInterval(interval);
    }, 40);
  }

  private updateRadiusCircle(
    center: google.maps.LatLng,
    radiusKm: number,
    zoom: number
  ): void {
    const radiusMeters = radiusKm * 1000;

    // 🎨 Color by zoom
    const color =
      zoom >= 15
        ? "#2ecc71" // green
        : zoom >= 13
        ? "#6cd397ff" // orange
        : "#6cd397ff"; // red

    // 🟢 CREATE circle
    if (!this.radiusCircle) {
      this.radiusCircle = new google.maps.Circle({
        map: this.map,
        center,
        radius: radiusMeters,

        icon: {
          path: "M 0,-1 0,1", // small dash
          strokeOpacity: 1,
          strokeColor: "#FF0000", // border color (red)
          scale: 3,
        },
        strokeOpacity: 1,
        strokeWeight: 2,

        fillColor: color,
        fillOpacity: 0.12,

        clickable: false,
      });

      this.startDashAnimation();
    } else {
      // 🔄 UPDATE
      this.radiusCircle.setCenter(center);
      this.radiusCircle.setRadius(radiusMeters);
      this.radiusCircle.setOptions({
        strokeColor: color,
        fillColor: color,
      });
    }
  }
  private startDashAnimation(): void {
    if (this.dashInterval) return;

    this.dashInterval = setInterval(() => {
      this.dashOffset = (this.dashOffset + 2) % 20;

      this.radiusCircle.setOptions({
        strokeOpacity: 0,
      });
    }, 60);
  }

  closeDriverPanel() {
    this.showDriverPanel = false;
    this.selectedDriver = null;
  }

  getDriver(id:any) {
    this.spinner.show();
    let data = {
      id: id,
    };
    this.driverService.getDriverByID(data).subscribe((result: any) => {
      if (result.statusCode == 200) {
        this.driverData = result.data;

        this.spinner.hide();
      } else {
        this.spinner.hide();
        this.toastr.error(result.message);
      }
    });
  }
}
